// Full-account backup: export every folder + card (+ bundled card images) into a
// self-contained .zip, and parse one back for restore. Image bytes travel inside the zip
// rather than as remote Storage URLs — if Firebase Storage itself is what broke, a URL
// reference would be worthless as a disaster-recovery backup.

import { safeImport } from './safeImport.js';

export const BACKUP_FORMAT_VERSION = 1;

const EXT_BY_MIME = {
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/png': 'png',
};

function extensionForMimeType(type) {
    return EXT_BY_MIME[type] || 'png';
}

// Fetches a card's image and adds it to the zip at images/{cardId}.{ext}. Returns the
// file name on success, or null on failure (caller flags this in the manifest — one bad
// image must never abort the whole backup).
async function addImageToZip(zip, cardId, imageUrl) {
    try {
        const res = await fetch(imageUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const fileName = `${cardId}.${extensionForMimeType(blob.type)}`;
        zip.file(`images/${fileName}`, blob);
        return fileName;
    } catch (_) {
        return null;
    }
}

// Pure data builder — no Firestore/network access. `imageFileNameByCardId` maps a card id
// to the zip-relative image file name already written by exportBackupZip (or is omitted
// entirely if the caller only wants a preview of what would be exported).
export function buildBackupManifest({ folders, savedExams, imageFileNameByCardId = {} } = {}) {
    return {
        formatVersion: BACKUP_FORMAT_VERSION,
        app: 'flashfonic',
        exportedAt: new Date().toISOString(),
        folders: (folders || []).map((f) => ({
            id: f.id,
            name: f.name,
            color: f.color || null,
            createdAt: f.createdAt ?? null,
            updatedAt: f.updatedAt ?? null,
            flashNotes: f.flashNotes || '',
            cards: (f.cards || []).map((c) => ({
                id: c.id,
                question: c.question,
                answer: c.answer,
                isFlagged: !!c.isFlagged,
                lastViewed: c.lastViewed ?? null,
                createdAt: c.createdAt ?? null,
                confidence: c.confidence ?? null,
                clinicalQuestionId: c.clinicalQuestionId ?? null,
                imageFileName: imageFileNameByCardId[c.id] || null,
                imageExportError: !!(c.imageUrl && !imageFileNameByCardId[c.id]),
            })),
        })),
        savedExams: (savedExams || []).map((e) => ({ ...e })),
        _savedExamsNote:
            'Saved exams are included for reference only and cannot be restored — exam attempts and their answer keys require the server.',
    };
}

// Builds and downloads flashfonic-backup-{date}.zip. `folders` must already be
// fully-hydrated (each folder's `cards` array populated) — the caller is responsible for
// resolving any folders that haven't been opened/loaded yet before calling this.
export async function exportBackupZip({ folders, savedExams, onProgress } = {}) {
    const JSZip = (await safeImport(() => import('jszip'), 'Backup export')).default;
    const zip = new JSZip();

    const imageCards = [];
    (folders || []).forEach((f) => (f.cards || []).forEach((c) => {
        if (c.imageUrl) imageCards.push(c);
    }));

    const total = imageCards.length;
    let done = 0;
    if (onProgress) onProgress({ done, total });

    const imageFileNameByCardId = {};
    for (const card of imageCards) {
        const fileName = await addImageToZip(zip, card.id, card.imageUrl);
        if (fileName) imageFileNameByCardId[card.id] = fileName;
        done += 1;
        if (onProgress) onProgress({ done, total });
    }

    const manifest = buildBackupManifest({ folders, savedExams, imageFileNameByCardId });
    zip.file('manifest.json', JSON.stringify(manifest, null, 2));

    const blob = await zip.generateAsync({ type: 'blob' });
    const dateStamp = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `flashfonic-backup-${dateStamp}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    return manifest;
}

// Unzips a backup file and returns { manifest, zip } — the zip handle is kept so image
// bytes can be pulled lazily during restore rather than all materialized in memory upfront.
export async function parseBackupZip(file) {
    const JSZip = (await safeImport(() => import('jszip'), 'Backup restore')).default;
    const zip = await JSZip.loadAsync(file);

    const manifestFile = zip.file('manifest.json');
    if (!manifestFile) throw new Error('Not a valid FlashFonic backup — no manifest.json found.');

    let manifest;
    try {
        manifest = JSON.parse(await manifestFile.async('string'));
    } catch (_) {
        throw new Error('Backup manifest is corrupted (invalid JSON).');
    }

    if (manifest.formatVersion !== BACKUP_FORMAT_VERSION) {
        throw new Error(
            `Unsupported backup version (${manifest.formatVersion ?? 'unknown'}). This app supports version ${BACKUP_FORMAT_VERSION}.`
        );
    }

    return { manifest, zip };
}

// Pulls one card's image bytes out of an already-parsed backup zip, or null if missing.
export async function getBackupImageBlob(zip, fileName) {
    if (!fileName) return null;
    const entry = zip.file(`images/${fileName}`);
    if (!entry) return null;
    return await entry.async('blob');
}
